import { FC, useState } from "react";

import { User as UserIcon } from "lucide-react";

import { User, UserCompany } from "@vviinn/user-managment-client-ts";

import { capitalize } from "../../helpers/string/capitalize";
import {
  ListWrapper,
  ListSettings,
  ListFilter,
  SortOptions,
} from "../../ui/layout/listPages";
import { DetailedCard } from "../../components/GeneralUI/DetailedCardComponents";
import { UserFilters, UsersSortOptions } from "../../user/types";
import {
  useAppDispatch,
  useAppSelector,
  setIsExtendedCard,
  setSortBy,
} from "../../store";
import { Badge } from "../../ui/layout/molecules/Badge/Badge";
import { sortUsers } from "../../user/helpers";

const LIST_TITLE = "Users";
const SINGLE_ITEM_TITLE = "user";

const sortOptions: UsersSortOptions = {
  name: "displayName",
  email: "email",
};

const filteringOptions: ListFilter<UserFilters, User>[] = [
  [
    {
      text: "Any status",
      rule: () => true,
    },
    {
      text: "Verified",
      rule: (user) => user.emailVerified === true,
    },
    {
      text: "Not verified",
      rule: (user) => user.emailVerified === false,
    },
  ],
  [
    {
      text: "Any role",
      rule: () => true,
    },
    {
      text: "Has role",
      rule: (user) => user.roles.length > 0,
    },
    {
      text: "Company role",
      rule: (user) => user.assignedCompanies.length > 0,
    },
    {
      text: "Global role",
      rule: (user) =>
        user.roles.some(
          (role) => role.includes("Global") || role.includes("Admin"),
        ),
    },
    {
      text: "No role",
      rule: (user) => user.roles.length === 0,
    },
  ],
];

const selectUsers = (
  users: User[],
  search: string,
  filters: UserFilters[],
  sortBy: keyof typeof sortOptions,
) => {
  const lowercaseSearch = search.toLowerCase();
  const sortKey = sortOptions[sortBy] as keyof User;

  let formattedUsers: User[] = [];
  for (let user of users) {
    formattedUsers.push({
      ...user,
      displayName: (user.displayName || user.email) ?? "",
      email: user.email ?? "",
    });
  }

  if (search)
    formattedUsers = formattedUsers.filter(
      (user) =>
        user &&
        ((user.email && user.email.toLowerCase().includes(lowercaseSearch)) ||
          (user.displayName &&
            user.displayName.toLowerCase().includes(lowercaseSearch))),
    );

  if (filters && filters.length > 0) {
    const relevantFilterOptions = filteringOptions
      .flat()
      .filter(({ text }) => filters.includes(text));
    formattedUsers = formattedUsers.filter((user) =>
      relevantFilterOptions.reduce(
        (accumulator, currentFilter) => accumulator && currentFilter.rule(user),
        true,
      ),
    );
  }
  return sortUsers(formattedUsers, sortKey);
};

const userIconColor = (roles: string[], emailVerified?: boolean) => {
  const hasRole = roles.length > 0;
  return emailVerified
    ? hasRole
      ? "bg-green-500"
      : "bg-amber-500"
    : "bg-red-500";
};

export const Users: FC<React.PropsWithChildren> = (): JSX.Element => {
  const users = useAppSelector((state) => state.users.users);
  const companies = useAppSelector((state) => state.companies.companies);
  const { isExtendedCard, sortBy, selectedFilters } = useAppSelector(
    (state) => state.ui.users,
  );

  const dispatch = useAppDispatch();

  const getCompanyName = (id: string | undefined) => {
    const nameList = companies.filter((company) => {
      return company.id === id;
    });
    const name = nameList.length > 0 ? nameList[0]?.name : "";
    return name ?? "";
  };

  const [search, setSearch] = useState("");
  const currentUsers = selectUsers(users, search, selectedFilters, sortBy);

  const setBadges = (
    roles: string[] = [],
    assignedCompanies: UserCompany[] = [],
  ) => {
    const relevantRoles = roles.filter((role) => !role.includes("Company"));
    return (
      <div className="mt-4 flex flex-row flex-wrap gap-2">
        {relevantRoles.length > 0 &&
          relevantRoles.map((role, index) => {
            return (
              <Badge
                type="neutral"
                title="Global"
                subtitle={role}
                key={index}
              />
            );
          })}
        {assignedCompanies.length > 0 &&
          assignedCompanies.map((company, index) => {
            return (
              company.id &&
              company.role && (
                <Badge
                  type="neutral"
                  title={getCompanyName(company.id)}
                  subtitle={"Company " + capitalize(company.role)}
                  key={index + relevantRoles.length + 1}
                />
              )
            );
          })}
        {relevantRoles.length === 0 && assignedCompanies.length === 0 && (
          <Badge type="warning" title="No roles assigned" key={0} />
        )}
      </div>
    );
  };

  return (
    <ListWrapper
      title={LIST_TITLE}
      newItemTitle={SINGLE_ITEM_TITLE}
      createPermissions="user:user:create"
    >
      <ListSettings
        title={LIST_TITLE.toLowerCase()}
        quantity={currentUsers.length}
        handleExtended={(value) => {
          dispatch(setIsExtendedCard({ feature: "users", value }));
        }}
        initExtended={isExtendedCard}
        sortOptions={Object.keys(sortOptions) as SortOptions[]}
        initSort={sortBy}
        handleSort={(value) => {
          dispatch(setSortBy({ feature: "users", value }));
        }}
        feature="users"
        listFilters={filteringOptions}
        initSearch={search}
        handleSearch={setSearch}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 lg:grid-cols-2">
        {currentUsers.map(
          (
            {
              displayName,
              uid,
              emailVerified,
              email,
              roles,
              assignedCompanies,
            },
            index,
          ) => {
            return (
              <DetailedCard
                title={displayName}
                link={`show/${uid}`}
                icon={<UserIcon />}
                specialIconStyle={`${userIconColor(
                  roles,
                  emailVerified,
                )} text-white`}
                key={index}
                subtitles={[
                  {
                    type: "link",
                    text: email,
                    link: `mailto:${email}`,
                  },
                ]}
                isExtended={isExtendedCard}
                children={setBadges(roles, assignedCompanies)}
              />
            );
          },
        )}
      </div>
    </ListWrapper>
  );
};
